import type { Document } from "../document/Document";
import type { GroupLayer, Layer } from "../document/Layer";
import type { Command } from "./Command";
import { GroupLayersCommand } from "./GroupLayersCommand";

// Replace a group with its children inline, at the group's position. Invert
// re-groups the same children back under the original group id/name.

export class UngroupCommand implements Command {
  readonly kind = "ungroup";
  private captured: { name: string; childIds: readonly string[] } | null = null;

  constructor(private readonly groupId: string) {}

  apply(doc: Document): Document {
    const group = findGroup(doc.layers, this.groupId);
    if (!group) return doc;
    this.captured = { name: group.name, childIds: group.children.map((c) => c.id) };
    return { ...doc, layers: ungroupIn(doc.layers, this.groupId) };
  }

  invert(before: Document, _after: Document): Command {
    const cap =
      this.captured ??
      (() => {
        const g = findGroup(before.layers, this.groupId);
        return g ? { name: g.name, childIds: g.children.map((c) => c.id) } : null;
      })();
    if (!cap) return new UngroupCommand(this.groupId);
    return new GroupLayersCommand(cap.childIds, this.groupId, cap.name);
  }
}

function findGroup(layers: readonly Layer[], id: string): GroupLayer | null {
  for (const l of layers) {
    if (l.kind === "group") {
      if (l.id === id) return l;
      const inner = findGroup(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}

function ungroupIn(layers: readonly Layer[], groupId: string): Layer[] {
  const out: Layer[] = [];
  for (const l of layers) {
    if (l.kind === "group" && l.id === groupId) {
      // Splice children in place.
      for (const c of l.children) out.push(c);
      continue;
    }
    if (l.kind === "group") {
      const group: GroupLayer = { ...l, children: ungroupIn(l.children, groupId) };
      out.push(group);
    } else {
      out.push(l);
    }
  }
  return out;
}
